'use client';

import { FliptClient } from '@flipt-io/flipt-client-js';

import type { EvaluationContext, FeatureFlagAdapter, FlagEvaluation } from './adapter.types';

const ANONYMOUS_ENTITY_ID = 'anonymous';

export interface FliptAdapterOptions {
  url: string;
  namespace?: string;
  apiToken?: string;
}

export class FliptAdapter implements FeatureFlagAdapter {
  readonly name = 'flipt';

  private client: FliptClient | null = null;

  private options: FliptAdapterOptions;

  constructor(options: FliptAdapterOptions) {
    this.options = options;
  }

  async initialize(): Promise<void> {
    if (this.client) {
      return;
    }

    this.client = await FliptClient.init({
      namespace: this.options.namespace ?? 'default',
      url: this.options.url,
      ...(this.options.apiToken && {
        authentication: { jwtToken: this.options.apiToken },
      }),
    });
  }

  async evaluate(flagKey: string, context: EvaluationContext): Promise<FlagEvaluation> {
    if (!this.client) {
      throw new Error('FliptAdapter used before initialize()');
    }

    const result = this.client.evaluateVariant({
      flagKey,
      entityId: context.userId ?? ANONYMOUS_ENTITY_ID,
      context: {},
    });

    return {
      flagKey,
      variant: result.variantKey || null,
      attachment: result.variantAttachment || null,
      match: result.match,
    };
  }
}
